"use client";

import { Modal, PreconceptionCard } from "@aph/ui-lib-humanx";
import React, { useEffect, useState } from "react";

import { addDays } from "date-fns/addDays";
import { differenceInDays } from "date-fns/differenceInDays";
import { isBefore } from "date-fns/isBefore";
import { useRouter } from "next/navigation";
import HorizontalCalendar from "@/components/HorizontalCalendar";
import { FirstFoldPreconception, HOME_TYPE } from "@/components/PageHome/api/interface";
import PreconceptionFlow from "@/components/PageHome/components/OnboardingSection/Flows/Preconception";
import PeriodCycle from "@/components/PageHome/components/OnboardingSection/Modals/PeriodCycle";
import PeriodDate from "@/components/PageHome/components/OnboardingSection/Modals/PeriodDate";
import styles from "@/components/PageHome/components/OnboardingSection/Onboarding.module.scss";
import { MORE_LESS_CYCLES } from "@/components/PageHome/constants";
import { onBoardUser } from "@/components/PageHome/redux/actions";
import { clearFlowData, setCurrentFlow, setFlowData } from "@/components/PageHome/redux/slice";
import { dateToString } from "@/helpers";
import {
  APPOINTMENT_URL,
  FLOW_SELECTION_POPUP,
  ONBOARDING_STATUS,
  PRECONCEPTION_AVERAGE_CYCLE_POPUP,
  PRECONCEPTION_DATE_POPUP,
} from "@/helpers/constants";
import routes from "@/helpers/router";
import { buttonClickedEvent, dateAddedEvent, popupViewedEvent } from "@/helpers/TSDEvents";
import { DeviceInfo } from "@/interface/deviceInfo";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { SignedinState } from "@/redux/slice/auth/enums/SignedInState";
import signedInStateSelector from "@/redux/slice/auth/selectors/signedInState";
import { Actions, ButtonClickedInterface } from "@/helpers/TSDEvents/interfaces";
import refererSelector from "@/redux/slice/global/selectors/refererSelector";

interface HorizontalCalendarRange {
  firstDate?: Date;
  lastDate?: Date;
}

interface UserDates {
  label: string;
  icon: string;
  onClick: () => void;
}

interface PreconceptionFirstFoldProps {
  userAgent?: DeviceInfo;
  data: FirstFoldPreconception;
}

const PreconceptionFirstFold: React.FC<PreconceptionFirstFoldProps> = ({ data, userAgent }) => {
  const dispatch = useAppDispatch();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [modalType, setModalType] = useState<string>("");
  const router = useRouter();
  const signedInStatus = useAppSelector(signedInStateSelector);
  const navSrcSelector = useAppSelector(refererSelector);

  const preconceptionUserData = data;

  const today: Date = new Date();
  const lastPeriod = preconceptionUserData?.firstDateOfLastPeriod || today;
  const firstDateOfLastPeriod = new Date(data?.firstDateOfLastPeriod);
  let averageCycle = preconceptionUserData?.averageCycle || 22;
  let averageCycleString = preconceptionUserData?.averageCycleDays;
  const cycleLength = Number(averageCycle);
  const isMoreLessCycle = MORE_LESS_CYCLES.includes(cycleLength);

  const getCalendarStartAndEndDates = (lastPeriod: any): HorizontalCalendarRange => {
    // Convert lastPeriod to Date if it's not already
    const lastPeriodDate = new Date(lastPeriod);

    // Calculate difference in days between today and last period
    const diffTime = today.getTime() - lastPeriodDate.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    let startDate;

    // Determine start date based on the first condition
    if (diffDays < cycleLength) {
      // If difference is less than cycle length, use first day of lastPeriod month
      startDate = new Date(lastPeriodDate);
    } else {
      // Calculate y = floor((today - lastPeriod) / cycle length)
      const y = Math.floor(diffDays / cycleLength);

      // Calculate first day = lastPeriod + y * cycle length
      startDate = new Date(lastPeriodDate);
      startDate.setDate(startDate.getDate() + y * cycleLength);
    }

    // Calculate end date = first date + 2 cycle lengths + 2
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + 2 * cycleLength + 2);

    return {
      firstDate: startDate,
      lastDate: endDate,
    };
  };

  const { firstDate, lastDate } = getCalendarStartAndEndDates(lastPeriod);

  const highlightedDates: Date[] = [];
  const dottedDates: Date[] = [];

  // Calculate all 5-day periods
  const totalDays = differenceInDays(new Date(lastDate), new Date(firstDate)) + 1;
  const cycles = Math.ceil(totalDays / cycleLength); // Number of cycles based on cycleLength

  // Loop through each cycle using Array.from()
  Array.from({ length: cycles }, (_, cycleIndex) => {
    const currentCycleStart = addDays(new Date(firstDate), cycleIndex * cycleLength); // Start of the current cycle

    // Generate dates for the 5-day period in the current cycle
    Array.from({ length: 5 }, (_, dayIndex) => {
      const currentDate = addDays(currentCycleStart, dayIndex);

      if (isBefore(currentDate, new Date(lastDate))) {
        // Check conditions to classify dates
        if (isBefore(currentDate, new Date(today))) {
          highlightedDates.push(currentDate);
        } else {
          dottedDates.push(currentDate);
        }
      }
    });
  });

  const fertileStartDate = preconceptionUserData?.fertileWindow?.start
    ? new Date(preconceptionUserData.fertileWindow?.start)
    : today;
  const fertileEndDate = preconceptionUserData?.fertileWindow?.end
    ? new Date(preconceptionUserData.fertileWindow?.end)
    : today;

  const range = `${dateToString(fertileStartDate, "do MMM")} to ${dateToString(fertileEndDate, "do MMM")}`;

  const handleClevertapTracking = (props: { buttonName: string; position?: number }) => {
    const { buttonName, position } = props;
    let payload: ButtonClickedInterface = {
      button_name: buttonName,
      momverse_stage: ONBOARDING_STATUS[HOME_TYPE.PRECONCEPTION],
      is_loggedin: signedInStatus === SignedinState.SIGNEDIN ? true : false,
    };

    if (position) {
      payload = { ...payload, button_position: position };
    }

    buttonClickedEvent(payload);
  };

  const ctaClickDateSelector = (date: any) => {
    if (date) {
      dateAddedEvent({
        action: Actions.EDIT,
        momverse_stage: ONBOARDING_STATUS[HOME_TYPE.PRECONCEPTION],
        is_loggedin: signedInStatus === SignedinState.SIGNEDIN ? true : false,
        date_type: "period date",
        date: date.getTime(),
      });
      handleClevertapTracking({
        buttonName: `Submit`,
      });

      const dateString = dateToString(date);
      dispatch(setFlowData({ cycleLength: averageCycle, periodDate: dateString }));
      dispatch(setCurrentFlow(HOME_TYPE.PRECONCEPTION));
      dispatch(onBoardUser());
      setIsModalOpen(false);
    }
  };

  const ctaClickCycleSelector = (cycleLength: any) => {
    if (cycleLength) {
      handleClevertapTracking({
        buttonName: `Submit`,
      });
      const dateString = dateToString(firstDateOfLastPeriod);
      dispatch(setFlowData({ periodDate: dateString, cycleLength: cycleLength }));
      dispatch(setCurrentFlow(HOME_TYPE.PRECONCEPTION));
      dispatch(onBoardUser());
      setIsModalOpen(false);
    }
  };

  let userDates: Array<UserDates> = [
    {
      label: `Add period date`,
      icon: `add`,
      onClick: () => {
        handleClevertapTracking({
          buttonName: `Add First day of last period`,
          position: 1,
        });
        dispatch(clearFlowData());
        dispatch(setCurrentFlow(HOME_TYPE.PRECONCEPTION));
        setModalType("wholeFlow");
        setIsModalOpen(true);
      },
    },
  ];

  if (preconceptionUserData?.firstDateOfLastPeriod) {
    userDates = [
      {
        label: `${!isMoreLessCycle ? (preconceptionUserData?.firstDateOfLastPeriod ? `First day of last period: ${dateToString(firstDateOfLastPeriod, "do MMM''yy")}` : "First day of last period") : ""}`,
        onClick: () => {
          handleClevertapTracking({
            buttonName: `Edit First day of last period`,
            position: 1,
          });

          dispatch(clearFlowData());
          dispatch(setCurrentFlow(HOME_TYPE.PRECONCEPTION));
          setModalType("dateSelector");
          setIsModalOpen(true);
        },
        icon: `${!isMoreLessCycle ? (preconceptionUserData?.firstDateOfLastPeriod ? `edit` : "add") : ""}`,
      },
      {
        label: `${preconceptionUserData?.averageCycle ? `Average cycle: ${isMoreLessCycle ? averageCycleString : `${preconceptionUserData?.averageCycle} days`}` : "Average cycle"}`,
        onClick: () => {
          handleClevertapTracking({
            buttonName: `Edit Average cycle`,
            position: 2,
          });

          dispatch(clearFlowData());
          dispatch(setCurrentFlow(HOME_TYPE.PRECONCEPTION));
          setModalType("cycleSelector");
          setIsModalOpen(true);
        },
        icon: `${preconceptionUserData?.averageCycle ? `edit` : "add"}`,
      },
    ];
  }

  const getSubtitle = (): string => {
    if (preconceptionUserData?.firstDateOfLastPeriod && !isMoreLessCycle) {
      return range;
    } else if (MORE_LESS_CYCLES.includes(Number(preconceptionUserData?.averageCycle))) {
      return "";
    } else {
      return "Share the first day of your last period and average cycle so that we can share personalised content that is meant for you.";
    }
  };

  const getTitleText = (): string => {
    if (preconceptionUserData?.firstDateOfLastPeriod && !isMoreLessCycle) {
      return "YOUR NEXT FERTILE WINDOW";
    } else if (isMoreLessCycle) {
      return "";
    } else {
      return "So excited for you!";
    }
  };

  const getCtaText = (): string => {
    if (
      preconceptionUserData?.firstDateOfLastPeriod &&
      !MORE_LESS_CYCLES.includes(Number(preconceptionUserData?.averageCycle))
    ) {
      return "Read about Ovulation";
    } else if (MORE_LESS_CYCLES.includes(Number(preconceptionUserData?.averageCycle))) {
      return "Book an appointment";
    } else {
      return "Read about preconception";
    }
  };

  const getCtaUrl = (): string => {
    if (
      preconceptionUserData?.firstDateOfLastPeriod &&
      !MORE_LESS_CYCLES.includes(Number(preconceptionUserData?.averageCycle))
    ) {
      return routes.pillar("ovulation");
    } else if (MORE_LESS_CYCLES.includes(Number(preconceptionUserData?.averageCycle))) {
      return APPOINTMENT_URL;
    } else {
      return routes.category("preconception");
    }
  };

  const handleTsdEvent = (arrow: string) => {
    handleClevertapTracking({
      buttonName: `preconception first fold calendar ${arrow} click`,
    });
  };

  const popupMessage = {
    dateSelector: PRECONCEPTION_DATE_POPUP,
    cycleSelector: PRECONCEPTION_AVERAGE_CYCLE_POPUP,
    wholeFlow: FLOW_SELECTION_POPUP,
  };

  useEffect(() => {
    if (isModalOpen) {
      // Hit popup viewed event
      popupViewedEvent({
        popup_name: popupMessage?.[modalType],
        popup_position: userAgent?.isMobile ? "bottom" : "top",
        momverse_stage: ONBOARDING_STATUS["PreconceptionHome"],
        nav_src: navSrcSelector,
      });
    }
  }, [isModalOpen]);

  return (
    <div className={`container spacingBetweenSections ${styles.firstFoldContainer}`}>
      <PreconceptionCard
        fertileInformation={
          <HorizontalCalendar
            userAgent={userAgent}
            {...{ firstDate, lastDate, highlightedDates, dottedDates }}
            handleTsdEvent={handleTsdEvent}
          />
        }
        isFertileDate={
          preconceptionUserData?.firstDateOfLastPeriod && preconceptionUserData.firstDateOfLastPeriod.length > 0
        }
        pregnancyDate={userDates}
        ctaText={getCtaText()}
        ctaClick={() => {
          handleClevertapTracking({ buttonName: getCtaText() });
          router.push(getCtaUrl());
        }}
        title={getTitleText()}
        productImgUrl={preconceptionUserData?.firstFoldImage}
        subTitle={getSubtitle()}
        averageCircleInfo={
          MORE_LESS_CYCLES.includes(cycleLength) ? (
            cycleLength === MORE_LESS_CYCLES[0] ? (
              <p>
                Since you have a shorter cycle, it might be better to <strong>check in with your doctor</strong> before
                we share fertility days
              </p>
            ) : (
              <p>
                Since you have a longer cycle, it might be better to <strong>check in with your doctor</strong> before
                we share fertility days
              </p>
            )
          ) : (
            ""
          )
        }
      />
      <Modal
        size="md"
        backdropClose
        mobileBottomAlign
        titleAlign="center"
        open={isModalOpen}
        handleOpen={(modalStatus: boolean) => {
          if (modalStatus === false) {
            setIsModalOpen(false);
          }
        }}
        isHXModal
        modalClass={styles.modalClass}
        titleClass={styles.titleClass}
        modalboxClass={styles.modalboxClass}
        modalContentClass={styles.innerSection}
      >
        {modalType === "dateSelector" && (
          <PeriodDate initialDate={firstDateOfLastPeriod} ctaClick={ctaClickDateSelector} ctaText={"Submit"} />
        )}
        {modalType === "cycleSelector" && <PeriodCycle initialValue={averageCycle} ctaClick={ctaClickCycleSelector} />}
        {modalType === "wholeFlow" && <PreconceptionFlow setIsModalOpen={setIsModalOpen} isModalOpen={isModalOpen} />}
      </Modal>
    </div>
  );
};

export default PreconceptionFirstFold;
